import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';

const API_KEY_STORAGE_KEY = 'externalIntegration_apiKey';

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401) return 'Integration not authorized. Please confirm your API key.';
  if (status === 404) return 'Lesson not found.';
  if (status === 500) return 'Server error. Please try again shortly.';
  return error?.response?.data?.error || error?.message || 'Unable to load lesson-linked questions.';
};

const ExternalLessonPastQuestionsPage = () => {
  const { lessonId } = useParams();
  const [apiKeyInput, setApiKeyInput] = useState(() => localStorage.getItem(API_KEY_STORAGE_KEY) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const canLoad = useMemo(() => Boolean(lessonId && apiKeyInput.trim()), [lessonId, apiKeyInput]);

  const fetchLessonQuestions = async () => {
    if (!canLoad) return;

    setLoading(true);
    setError('');

    try {
      localStorage.setItem(API_KEY_STORAGE_KEY, apiKeyInput.trim());
      const response = await api.get(`/public/lessons/${lessonId}/past-questions`, {
        headers: {
          'x-api-key': apiKeyInput.trim()
        }
      });
      setPayload(response.data || null);
    } catch (err) {
      setPayload(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h1 className="text-2xl font-bold text-gray-900">Lesson Past Questions</h1>
          <p className="text-sm text-gray-600 mt-1">View lesson-linked similar past questions and topic hint.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
            <div>
              <label htmlFor="external-lesson-id" className="block text-sm font-medium text-gray-700 mb-1">Lesson ID</label>
              <input
                id="external-lesson-id"
                value={lessonId || ''}
                readOnly
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-gray-50"
              />
            </div>
            <div>
              <label htmlFor="external-lesson-api-key" className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <input
                id="external-lesson-api-key"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter external integration API key"
                type="password"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={fetchLessonQuestions}
              disabled={!canLoad || loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {loading ? 'Loading...' : 'Load Linked Questions'}
            </button>
            <Link to="/external/courses/1/lessons" className="text-sm text-blue-700 hover:text-blue-900">
              ← Back to course lessons
            </Link>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}
        </div>

        {payload && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Topic Hint</p>
              <p className="text-gray-900 font-medium mt-1">{payload.topicHint || 'No topic hint provided.'}</p>
              <p className="mt-2 text-sm">
                <span className="font-medium text-gray-700">Linked:</span>{' '}
                {payload.enabled ? (
                  <span className="text-green-700">Enabled</span>
                ) : (
                  <span className="text-gray-600">Disabled</span>
                )}
              </p>
            </div>

            {!payload.enabled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-yellow-800 text-sm">
                This lesson has not enabled past-question linkage yet.
              </div>
            )}

            {payload.enabled && (!payload.similarQuestions || payload.similarQuestions.length === 0) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center text-gray-600">
                No similar questions found for this lesson.
              </div>
            )}

            {payload.enabled && Array.isArray(payload.similarQuestions) && payload.similarQuestions.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Similar Questions</h3>
                  <span className="text-sm text-gray-500">{payload.similarQuestions.length} found</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {payload.similarQuestions.map((question, index) => (
                    <div key={`${question.id}-${index}`} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-medium text-gray-900 line-clamp-2">{question.text || 'Untitled question'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {question.subject || 'Unknown subject'} • {question.year || 'N/A'} • {question.section || 'N/A'}
                        </p>
                      </div>
                      <Link
                        to={`/external/questions/${question.id}/bot`}
                        className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium"
                      >
                        Ask Bot
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalLessonPastQuestionsPage;
